import { Button } from "@svea/design-system-components-react";
import {
	assertDefined,
	formatBgNumber,
	P,
	Stack,
	useText,
	Dialog,
	ButtonGroup,
	useAppForm,
	Heading,
	ProductSku,
} from "@sveabank/cc-common";
import { useState } from "react";
import { usePageContext } from "../../hooks/context";
import { usePackage } from "../../hooks/package";
import { useSteps } from "../../hooks/useSteps";
import type { BankGiroFormValues } from "../../types/bankgiro";
import { stepIs } from "../../utils/application";
import { validateBgNumber } from "../../utils/validation";
import { isCreditWorthy } from "./helpers";
import { useChoosePackage } from "./hooks";

type Props = {
	bankGiroFormValues: BankGiroFormValues | undefined;
	validateSteps: (bgValues?: BankGiroFormValues) => boolean;
};

const formatBgConfig = (values: BankGiroFormValues): Required<BankGiroFormValues> | undefined => {
	switch (values.answer) {
		case "notInterested":
		case "createNew":
			return {
				answer: values.answer,
				existingNumber: null,
			};
		case "transferExisting":
			return {
				answer: values.answer,
				existingNumber: assertDefined(values.existingNumber),
			};
		default:
			return undefined;
	}
};

const BankgiroSettings = ({ bankGiroFormValues, validateSteps }: Props) => {
	const [t, texts] = useText();
	const { selectedPackageId, hasCreditProduct, hasCheckCreditProduct } = usePackage();
	const { configureBankGiro } = usePageContext(useChoosePackage);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [formValues, setFormValues] = useState<BankGiroFormValues | undefined>(undefined);
	const { steps } = useSteps();
	const creditScore = steps?.find(stepIs("CreditScoreStep"))?.state;
	const isRejected = !creditScore?.assessment || !isCreditWorthy(creditScore.assessment);

	const submitBankGiroForm = async (values: BankGiroFormValues) => {
		const bgValues = formatBgConfig(values);
		if (validateSteps(bgValues) && bgValues) {
			await configureBankGiro(bgValues);
		}
	};

	const onSubmit = async (values: BankGiroFormValues) => {
		setFormValues(values);

		if (!isRejected && !hasCreditProduct) {
			setIsDialogOpen(true);
		} else {
			submitBankGiroForm(values);
		}
	};

	const scrollToCreditAddons = () => {
		const el = document.getElementById("choose-addons");

		if (!el) return;
		setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }));
	};

	const onContinueClick = () => {
		setIsDialogOpen(false);
		submitBankGiroForm(formValues!);
	};

	const onCancelClick = () => {
		setIsDialogOpen(false);
		scrollToCreditAddons();
	};

	const form = useAppForm({
		defaultValues: {
			answer: bankGiroFormValues?.answer ?? "createNew",
			existingNumber: bankGiroFormValues?.existingNumber ?? "",
		},
		onSubmit: ({ value }) => {
			const mappedValues: BankGiroFormValues =
				value.answer === "transferExisting"
					? {
							answer: value.answer,
							existingNumber: value.existingNumber,
						}
					: {
							answer: value.answer,
							existingNumber: null,
						};

			onSubmit(mappedValues);
		},
	});

	return (
		selectedPackageId && (
			<>
				<Stack gap={4}>
					<P>{t(texts.choosePackage.companyAccount.description)}</P>

					<Heading.H4 className="text-primary">
						{t(texts.choosePackage.companyAccount.subheading)}
					</Heading.H4>

					<form
						id="bankgiroForm"
						onSubmit={(e) => {
							e.preventDefault();
							form.handleSubmit();
						}}
					>
						<form.AppForm>
							<Stack gap={4}>
								<form.AppField
									name="answer"
									children={(field) => (
										<field.RadioGroupField
											options={[
												{
													label: t(texts.choosePackage.companyAccount.answer1),
													value: "transferExisting",
												},
												{
													label: t(texts.choosePackage.companyAccount.answer2),
													value: "createNew",
												},
												...(!hasCheckCreditProduct
													? [
															{
																label: t(texts.choosePackage.companyAccount.answer3),
																value: "notInterested",
															},
														]
													: []),
											]}
										/>
									)}
								/>
								<form.AppField
									name="answer"
									children={(field) => (
										<field.Condition when="answer" is="transferExisting" fieldName="existingNumber">
											<form.AppField
												name="existingNumber"
												validators={{
													onSubmit: ({ value }) => {
														if (!value) {
															return { message: "Fyll i det här fältet" };
														}

														const invalidBg = validateBgNumber(value);
														if (invalidBg) {
															return { message: t(invalidBg) };
														}

														return undefined;
													},
												}}
												children={(field) => (
													<field.TextField
														label={t(texts.choosePackage.companyAccount.inputLabel)}
														type="tel"
														placeholder="1234-1244"
														name="existingNumber"
														value={field.state.value || ""}
														onChange={(e) => field.handleChange(e.target.value || "")}
														onBlur={() => {
															field.handleChange(formatBgNumber(field.state.value || ""));
															field.handleBlur();
														}}
													/>
												)}
											/>
										</field.Condition>
									)}
								/>
							</Stack>
						</form.AppForm>
					</form>
				</Stack>

				<Dialog
					isDismissable={false}
					size="small"
					center
					isOpen={isDialogOpen}
					heading={t(texts.chooseAddons.creditDialog.title)}
					icon="info"
				>
					<Dialog.Content>{t(texts.chooseAddons.creditDialog.body)}</Dialog.Content>
					<ButtonGroup>
						<Button
							onPress={onCancelClick}
							data-tracking-action="Onboarding: Credit dialog - Stay on page"
							data-tracking-name="Credit dialog"
						>
							{t(texts.chooseAddons.creditDialog.cancelButton)}
						</Button>
						<Button
							onPress={onContinueClick}
							variant="secondary"
							data-tracking-action="Onboarding: Credit dialog - Go to the next page"
							data-tracking-name="Credit dialog"
						>
							{t(texts.chooseAddons.creditDialog.continueButton)}
						</Button>
					</ButtonGroup>
				</Dialog>
			</>
		)
	);
};

export default BankgiroSettings;
